/**
 * Lorem Ipsum Generator Tool
 * 
 * Generates placeholder text.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { IGeneratorTool } from '@/interfaces/ITool';

export const LoremIpsumTool: IGeneratorTool = {
  id: 'lorem-ipsum',
  name: 'Lorem Ipsum',
  description: 'Generate placeholder text',
  version: '1.0.0',
  type: 'generator',
  category: 'Generators',
  keywords: ['lorem', 'ipsum', 'placeholder', 'text', 'dummy', 'generator'],

  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'count',
      type: 'number',
      label: 'Count',
      defaultValue: 3,
      validation: { min: 1, max: 100 },
      description: 'Number of units to generate',
    },
    {
      name: 'unit',
      type: 'select',
      label: 'Unit',
      defaultValue: 'paragraphs',
      options: ['words', 'sentences', 'paragraphs'],
      description: 'Type of text units to generate',
    },
    {
      name: 'startWithLorem',
      type: 'boolean',
      label: 'Start with "Lorem ipsum"',
      defaultValue: true,
      description: 'Begin text with the classic phrase',
    },
  ],

  async generate(config: Record<string, unknown>): Promise<string> {
    const count = (config.count as number) || 3;
    const unit = (config.unit as string) || 'paragraphs';
    const startWithLorem = config.startWithLorem !== false;

    switch (unit) {
      case 'words':
        return generateWords(count, startWithLorem);
      case 'sentences':
        return generateSentences(count, startWithLorem);
      case 'paragraphs':
      default:
        return generateParagraphs(count, startWithLorem);
    }
  },
};

// Lorem ipsum word list
const WORDS = [
  'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
  'sed', 'do', 'eiusmod', 'tempor', 'incididunt', 'ut', 'labore', 'et', 'dolore',
  'magna', 'aliqua', 'enim', 'ad', 'minim', 'veniam', 'quis', 'nostrud',
  'exercitation', 'ullamco', 'laboris', 'nisi', 'aliquip', 'ex', 'ea', 'commodo',
  'consequat', 'duis', 'aute', 'irure', 'in', 'reprehenderit', 'voluptate',
  'velit', 'esse', 'cillum', 'fugiat', 'nulla', 'pariatur', 'excepteur', 'sint',
  'occaecat', 'cupidatat', 'non', 'proident', 'sunt', 'culpa', 'qui', 'officia',
  'deserunt', 'mollit', 'anim', 'id', 'est', 'laborum', 'vivamus', 'lacus',
  'vel', 'augue', 'laoreet', 'rutrum', 'faucibus', 'auctor', 'dui', 'proin',
  'gravida', 'hendrerit', 'lectus', 'vestibulum', 'mattis', 'ullamcorper',
  'blandit', 'libero', 'volutpat', 'nibh', 'cras', 'pulvinar', 'porta',
  'pellentesque', 'habitant', 'morbi', 'tristique', 'senectus', 'netus',
];

function getRandomWord(): string {
  return WORDS[Math.floor(Math.random() * WORDS.length)];
}

function generateWords(count: number, startWithLorem: boolean): string {
  const words: string[] = [];
  
  if (startWithLorem) {
    words.push('lorem', 'ipsum');
  }

  while (words.length < count) {
    words.push(getRandomWord());
  }

  return words.slice(0, count).join(' ');
}

function generateSentence(startWithLorem: boolean): string {
  const wordCount = 8 + Math.floor(Math.random() * 10);
  const words: string[] = [];

  if (startWithLorem) {
    words.push('Lorem', 'ipsum', 'dolor', 'sit', 'amet');
  }

  while (words.length < wordCount) {
    words.push(getRandomWord());
  }

  // Capitalize first word
  words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);

  return words.join(' ') + '.';
}

function generateSentences(count: number, startWithLorem: boolean): string {
  const sentences: string[] = [];

  for (let i = 0; i < count; i++) {
    sentences.push(generateSentence(i === 0 && startWithLorem));
  }

  return sentences.join(' ');
}

function generateParagraph(startWithLorem: boolean): string {
  const sentenceCount = 4 + Math.floor(Math.random() * 4);
  const sentences: string[] = [];

  for (let i = 0; i < sentenceCount; i++) {
    sentences.push(generateSentence(i === 0 && startWithLorem));
  }

  return sentences.join(' ');
}

function generateParagraphs(count: number, startWithLorem: boolean): string {
  const paragraphs: string[] = [];

  for (let i = 0; i < count; i++) {
    paragraphs.push(generateParagraph(i === 0 && startWithLorem));
  }

  return paragraphs.join('\n\n');
}

